import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Avatar } from "../components/Avatar";
import { inputCls } from "../components/Chips";
import { useAuth } from "../context/AuthContext";
import { errMsg, http } from "../lib/api";

const labelCls = "block text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2";
const btnCls = "flex items-center gap-2 px-6 py-2.5 rounded-full bg-[var(--c-orange)] text-white text-sm font-semibold hover:bg-[#ff7a26] transition-colors disabled:opacity-60";
const cardCls = "p-6 sm:p-8 rounded-3xl border border-[var(--c-line)] bg-[var(--c-surface)] space-y-5";

const ProfileForm = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [bio, setBio] = useState(user.bio || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setUser((await http.patch("/profile", { name: name.trim(), bio })).data);
      toast.success("Profilis atnaujintas");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="profile-form" className={cardCls}>
      <div>
        <label className={labelCls} htmlFor="pf-name">Rodomas vardas</label>
        <input id="pf-name" data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} required className={inputCls} />
        <p className="text-xs text-zinc-500 mt-2">Vardas bus atnaujintas ir prie visų jūsų dainų.</p>
      </div>
      <div>
        <label className={labelCls} htmlFor="pf-bio">Apie mane</label>
        <textarea id="pf-bio" data-testid="profile-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={300} rows={4}
          className={`${inputCls} resize-none`} placeholder="Keli žodžiai apie jus ir jūsų muziką" />
        <p className="text-xs text-zinc-500 mt-1 text-right font-mono">{bio.length}/300</p>
      </div>
      <button data-testid="profile-save-button" disabled={busy || !name.trim()} className={btnCls}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Išsaugoti
      </button>
    </form>
  );
};

export default function Profile() {
  const { user } = useAuth();
  return (
    <div className="max-w-3xl space-y-8 rise">
      <header className="flex flex-col sm:flex-row sm:items-center gap-6">
        <Avatar user={user} className="w-24 h-24 text-4xl" testId="profile-avatar" />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold mb-2">Paskyra</p>
          <h1 data-testid="profile-title" className="font-display text-4xl sm:text-5xl font-bold truncate">{user.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">{user.email}{user.has_password ? "" : " · Google paskyra"}</p>
          <Link to={`/autorius/${user.user_id}`} data-testid="profile-public-link" className="inline-flex items-center gap-1.5 mt-3 text-sm text-[var(--c-orange)] hover:underline">
            <ExternalLink className="w-3.5 h-3.5" />Mano viešas puslapis
          </Link>
        </div>
      </header>
      <ProfileForm />
    </div>
  );
}
